/**
 * Odoscope attribution data - captures marketing campaign parameters and traffic source
 * for hyper-personalization and customer engagement analytics.
 */
export type OdoscopeAttributionData = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  clientReferrer?: string;
};

// Cookie name for Odoscope UTM parameters and referrer tracking
export const ODOSCOPE_PARAMS_COOKIE_NAME = 'odoscope-params';

/**
 * Captures Odoscope attribution data from URL parameters and referrer
 * @returns Attribution data to persist, or undefined if nothing to capture
 */
export const captureAttributionData = ({
  searchParams,
  clientReferrer,
  domain,
  existingCookieValue,
}: {
  searchParams: URLSearchParams | null;
  clientReferrer: string;
  domain: string;
  existingCookieValue?: string;
}): OdoscopeAttributionData | undefined => {
  if (!searchParams) return undefined;

  // 1. Extract UTMs
  const utmSource = searchParams.get('utm_source');
  const utmMedium = searchParams.get('utm_medium');
  const utmCampaign = searchParams.get('utm_campaign');

  // 2. Check if referrer is external (simple hostname extraction)
  let isExternal = false;
  if (
    clientReferrer &&
    (clientReferrer.startsWith('http://') || clientReferrer.startsWith('https://'))
  ) {
    // Extract hostname: "https://google.com/search" -> "google.com"
    let hostname: string | undefined;

    try {
      const url = new URL(clientReferrer);
      hostname = url.hostname;
    } catch {
      // Fallback for malformed URLs
      hostname = clientReferrer.split('/')[2]?.split(':')[0];
    }

    if (hostname) {
      isExternal = hostname !== domain && !hostname?.endsWith(`.${domain}`);
    }
  }

  // 3. Guard: Nothing to capture
  if (!utmSource && !utmMedium && !utmCampaign && !isExternal) {
    return undefined;
  }

  // 4. Merge with existing data
  let existingData: OdoscopeAttributionData = {};
  if (existingCookieValue) {
    try {
      existingData = JSON.parse(existingCookieValue) as OdoscopeAttributionData;
    } catch {
      // Ignore malformed cookie data - start fresh
    }
  }

  return {
    ...existingData,
    ...(utmSource && { utmSource }),
    ...(utmMedium && { utmMedium }),
    ...(utmCampaign && { utmCampaign }),
    ...(isExternal && { clientReferrer }),
  };
};
